(() => {
  'use strict';
  const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const config = (() => { try { return JSON.parse(document.getElementById('DropkyConfig')?.textContent || '{}'); } catch { return {}; } })();
  const allowedPayPalHosts = new Set(['paypal.com', 'www.paypal.com', 'paypal.me', 'www.paypal.me']);
  function paypalUrl(value) {
    try {
      if (typeof value !== 'string' || !value.startsWith('https://')) return '';
      const url = new URL(value);
      if (url.protocol !== 'https:' || url.username || url.password || (url.port && url.port !== '443') || !allowedPayPalHosts.has(url.hostname)) return '';
      return value;
    } catch { return ''; }
  }
  function analyticsAllowed() {
    return config.analytics && !config.designMode && window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
  }
  function track(event, data) {
    if (!analyticsAllowed()) return false;
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event, ...data });
      window.dispatchEvent(new CustomEvent('dropky:analytics', { detail: { event, ...data } }));
      return true;
    } catch { return false; }
  }
  if (config.analytics && !window.Shopify?.customerPrivacy && window.Shopify?.loadFeatures) {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => document.dispatchEvent(new Event('dropky:privacy-ready')));
  }
  const seenProducts = new Set();

  class DropkyHeader extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const opts = { signal: this.abort.signal };
      this.menu = this.querySelector('[data-menu-panel]');
      this.search = this.querySelector('[data-search-panel]');
      this.menuButton = this.querySelector('[data-open-menu]');
      this.searchButton = this.querySelector('[data-open-search]');
      this.closest('.shopify-section')?.classList.toggle('dk-header-wrap--sticky', this.classList.contains('dk-header--sticky'));
      this.menuButton?.addEventListener('click', () => this.toggle('menu'), opts);
      this.searchButton?.addEventListener('click', () => this.toggle('search'), opts);
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          const opener = !this.search?.hidden ? this.searchButton : this.menuButton;
          this.close(); opener?.focus();
        }
      }, opts);
      document.addEventListener('click', (event) => { if (!this.contains(event.target)) this.close(); }, opts);
      this.menu?.addEventListener('click', (event) => { if (event.target.closest('a')) this.close(); }, opts);
    }
    toggle(which) {
      const panel = this[which];
      if (!panel) return;
      const open = panel.hidden;
      this.close();
      panel.hidden = !open;
      this[`${which}Button`]?.setAttribute('aria-expanded', String(open));
      if (open && which === 'search') panel.querySelector('input')?.focus();
    }
    close() {
      for (const key of ['menu', 'search']) {
        if (this[key]) this[key].hidden = true;
        this[`${key}Button`]?.setAttribute('aria-expanded', 'false');
      }
    }
    disconnectedCallback() { this.abort?.abort(); }
  }

  class DropkySearch extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[role="listbox"]');
      this.status = this.querySelector('[data-search-status]');
      if (!this.input || !this.results) return;
      this.active = -1;
      this.cache = new Map();
      const opts = { signal: this.abort.signal };
      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.request?.abort();
        this.close();
        const query = this.input.value.trim();
        if (query.length < 2) return;
        this.timer = setTimeout(() => this.load(query), 180);
      }, opts);
      this.input.addEventListener('keydown', (event) => this.keydown(event), opts);
      this.input.addEventListener('focus', () => { if (this.results.children.length && this.input.value.trim().length >= 2) this.open(); }, opts);
      document.addEventListener('click', (event) => { if (!this.contains(event.target)) this.close(); }, opts);
      this.results.addEventListener('pointermove', (event) => {
        const hit = event.target.closest('[role="option"]');
        if (hit) this.activate(this.options().indexOf(hit));
      }, opts);
      this.addEventListener('focusout', (event) => { if (!this.contains(event.relatedTarget)) this.close(); }, opts);
    }
    async load(query) {
      if (this.cache.has(query)) { this.render(this.cache.get(query)); return; }
      this.request = new AbortController();
      this.results.setAttribute('aria-busy', 'true');
      this.status.textContent = 'Căutăm jocul…';
      try {
        const endpoint = new URL(`${this.dataset.endpoint || '/search/suggest'}.json`, location.origin);
        endpoint.search = new URLSearchParams({ q: query, 'resources[type]': 'product', 'resources[limit]': '6', 'resources[options][unavailable_products]': 'last', 'resources[options][fields]': 'title,product_type,variants.title' }).toString();
        const response = await fetch(endpoint, { signal: this.request.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('search');
        const json = await response.json();
        const products = json.resources?.results?.products || [];
        if (query !== this.input.value.trim()) return;
        if (this.cache.size >= 20) this.cache.delete(this.cache.keys().next().value);
        this.cache.set(query, products);
        this.render(products);
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.close();
          this.status.textContent = 'Apasă Enter pentru a vedea rezultatele căutării.';
        }
      } finally { this.results.removeAttribute('aria-busy'); }
    }
    render(products) {
      this.results.replaceChildren();
      for (const [index, product] of products.entries()) {
        let url;
        try { url = new URL(product.url, location.origin); } catch { continue; }
        if (url.origin !== location.origin || !/(?:^|\/)products\//.test(url.pathname)) continue;
        const anchor = document.createElement('a');
        anchor.href = url.href; anchor.className = 'dk-search-result';
        anchor.id = `${this.results.id}-${index}`;
        anchor.setAttribute('role', 'option'); anchor.setAttribute('aria-selected', 'false'); anchor.tabIndex = -1;
        const imageUrl = product.featured_image?.url || product.image;
        if (imageUrl) {
          let parsed;
          try { parsed = new URL(imageUrl, location.origin); } catch { parsed = null; }
          if (parsed && ['https:', 'http:'].includes(parsed.protocol)) {
            const image = document.createElement('img'); image.src = parsed.href; image.width = 40; image.height = 52; image.alt = ''; image.loading = 'lazy';
            anchor.append(image);
          }
        }
        const title = document.createElement('span'); title.textContent = product.title; anchor.append(title);
        this.results.append(anchor);
      }
      const count = this.options().length;
      if (!count) { const empty = document.createElement('p'); empty.className = 'dk-search-message'; empty.textContent = 'Nu am găsit jocul. Încearcă un alt nume.'; this.results.append(empty); }
      this.status.textContent = `${count} jocuri găsite.`;
      this.active = -1; this.input.removeAttribute('aria-activedescendant'); this.open();
    }
    options() { return [...this.results.querySelectorAll('[role="option"]')]; }
    open() { this.results.hidden = false; this.input.setAttribute('aria-expanded', 'true'); }
    close() { this.results.hidden = true; this.input.setAttribute('aria-expanded', 'false'); this.input.removeAttribute('aria-activedescendant'); this.active = -1; }
    activate(index) {
      const options = this.options(); if (!options.length) return;
      this.active = (index + options.length) % options.length;
      options.forEach((option, i) => option.setAttribute('aria-selected', String(i === this.active)));
      this.input.setAttribute('aria-activedescendant', options[this.active].id);
      options[this.active].scrollIntoView({ block: 'nearest' });
    }
    keydown(event) {
      if (event.key === 'Escape') { this.close(); return; }
      if (this.results.hidden) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); this.activate(this.active + (event.key === 'ArrowDown' ? 1 : -1)); }
      if (event.key === 'Enter' && this.active >= 0) { event.preventDefault(); this.options()[this.active]?.click(); }
    }
    disconnectedCallback() { this.abort?.abort(); this.request?.abort(); clearTimeout(this.timer); }
  }

  class DropkyGallery extends HTMLElement {
    connectedCallback() {
      this.abort?.abort(); this.abort = new AbortController();
      const opts = { signal: this.abort.signal };
      this.track = this.querySelector('[data-gallery-track]');
      this.dialog = this.querySelector('[data-zoom-dialog]');
      this.image = this.querySelector('[data-zoom-image]');
      this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.move(-1), opts);
      this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.move(1), opts);
      this.track?.addEventListener('pointerdown', (event) => { this.pointerStart = event.clientX; }, opts);
      this.track?.addEventListener('click', (event) => {
        if (Math.abs(event.clientX - (this.pointerStart ?? event.clientX)) > 12) return;
        const slide = event.target.closest('[data-zoom-src]');
        if (!slide || !this.dialog?.showModal || !this.image) return;
        this.image.src = slide.dataset.zoomSrc;
        this.image.alt = slide.querySelector('img')?.alt || '';
        this.dialog.showModal();
        this.dialog.querySelector('button')?.focus();
      }, opts);
      this.querySelector('[data-zoom-close]')?.addEventListener('click', () => this.dialog?.close(), opts);
      this.dialog?.addEventListener('click', (event) => { if (event.target === this.dialog) this.dialog.close(); }, opts);
      this.track?.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); this.move(event.key === 'ArrowLeft' ? -1 : 1); }
      }, opts);
    }
    move(direction) {
      if (!this.track) return;
      const last = this.track.children.length - 1;
      const current = Math.round(this.track.scrollLeft / this.track.clientWidth);
      const index = Math.max(0, Math.min(last, current + direction));
      this.track.scrollTo({ left: index * this.track.clientWidth, behavior: reducedMotion() ? 'instant' : 'smooth' });
    }
    showMedia(id) {
      if (!id || !this.track) return;
      const slides = [...this.track.children]; const index = slides.findIndex(slide => String(slide.dataset.mediaId) === String(id));
      if (index >= 0) this.track.scrollTo({ left: index * this.track.clientWidth, behavior: reducedMotion() ? 'instant' : 'smooth' });
    }
    disconnectedCallback() { this.abort?.abort(); if (this.dialog?.open) this.dialog.close(); }
  }

  class DropkyProduct extends HTMLElement {
    connectedCallback() {
      this.abort?.abort(); this.abort = new AbortController();
      const opts = { signal: this.abort.signal };
      try { this.data = JSON.parse(this.querySelector('[data-product-data]').textContent); } catch { return; }
      this.data.variants.forEach(v => { v.paypalUrl = paypalUrl(v.paypalUrl); });
      this.variant = this.data.variants.find(v => String(v.id) === String(this.data.initialVariant)) || null;
      this.selectors = [...this.querySelectorAll('[data-option-index]')];
      this.addEventListener('change', (event) => { if (event.target.matches('.dk-option input[type="radio"]')) this.selectVariant(); }, opts);
      this.addEventListener('click', (event) => this.buy(event), opts);
      window.addEventListener('pageshow', () => this.refresh(), opts);
      this.viewProduct();
      document.addEventListener('visitorConsentCollected', () => this.viewProduct(), opts);
      document.addEventListener('dropky:privacy-ready', () => this.viewProduct(), opts);
      this.refresh();
      this.querySelector('dk-gallery')?.showMedia(this.variant?.mediaId || this.data.defaultMediaId);
      this.initSticky();
    }
    eventData() {
      return { product_id: this.data.id, product_name: this.data.title, variant_id: this.variant?.id || null, variant_name: this.variant?.title || null, value: this.variant ? this.variant.price / 100 : null, currency: this.data.currency, quantity: 1 };
    }
    viewProduct() {
      if (!seenProducts.has(this.data.id) && track('dropky_product_view', this.eventData())) seenProducts.add(this.data.id);
    }
    selectVariant() {
      const values = this.selectors.map(field => field.querySelector('input:checked')?.value || '');
      this.variant = this.data.variants.find(v => v.options.every((value, index) => value === values[index])) || null;
      this.refresh();
      this.querySelector('dk-gallery')?.showMedia(this.variant?.mediaId || this.data.defaultMediaId);
      const url = new URL(location.href);
      if (this.variant) url.searchParams.set('variant', this.variant.id); else url.searchParams.delete('variant');
      history.replaceState({}, '', url);
      track('dropky_select_variant', this.eventData());
      this.dispatchEvent(new CustomEvent('dropky:variant-change', { bubbles: true, detail: { productId: this.data.id, variantId: this.variant?.id || null } }));
    }
    refresh() {
      const v = this.variant; const canBuy = !!(v?.available && v.paypalUrl);
      const hasDiscount = v && v.compare > v.price;
      this.querySelectorAll('[data-price]').forEach(slot => { slot.textContent = v?.priceText || '—'; });
      this.querySelectorAll('[data-compare]').forEach(slot => { slot.hidden = !hasDiscount; slot.textContent = v?.compareText || ''; });
      this.querySelectorAll('[data-discount]').forEach(slot => { slot.hidden = !hasDiscount; slot.textContent = hasDiscount ? `−${Math.round((v.compare - v.price) / v.compare * 100)}%` : ''; });
      this.querySelectorAll('[data-savings]').forEach(slot => { slot.hidden = !hasDiscount; slot.textContent = hasDiscount ? `Economisești ${v.savingsText}` : ''; });
      this.querySelectorAll('[data-paypal-cta]').forEach(cta => {
        cta.removeAttribute('aria-busy');
        if (canBuy) { cta.href = v.paypalUrl; cta.removeAttribute('aria-disabled'); cta.removeAttribute('tabindex'); }
        else { cta.removeAttribute('href'); cta.setAttribute('aria-disabled', 'true'); cta.tabIndex = -1; }
        const label = cta.querySelector('[data-buy-label]'); if (label) label.textContent = canBuy ? this.data.buyText : this.data.unavailableText;
      });
      const available = this.querySelector('[data-availability]');
      if (available) available.textContent = !v ? 'Combinația aleasă nu este disponibilă. Încearcă o altă variantă.' : !v.available ? this.data.soldOutText : !v.paypalUrl ? this.data.missingText : 'Varianta aleasă este disponibilă.';
      const values = this.selectors.map(field => field.querySelector('input:checked')?.value || '');
      for (const [index, field] of this.selectors.entries()) {
        const currentLabel = field.querySelector('[data-option-label]'); if (currentLabel) currentLabel.textContent = values[index];
        for (const input of field.querySelectorAll('input')) {
          const possible = this.data.variants.some(candidate => candidate.options.every((value, i) => value === (i === index ? input.value : values[i])) && candidate.available && candidate.paypalUrl);
          input.closest('label').classList.toggle('is-unavailable', !possible);
        }
      }
      this.querySelectorAll('[data-selection]').forEach(slot => { slot.textContent = this.data.defaultVariant ? this.data.title : values.join(' · '); });
      const status = this.querySelector('[data-variant-status]');
      if (status) status.textContent = v ? `${v.title === 'Default Title' ? this.data.title : v.title}. ${v.priceText}. ${canBuy ? 'Disponibil.' : this.data.unavailableText}` : 'Combinație indisponibilă.';
    }
    buy(event) {
      const cta = event.target.closest('[data-paypal-cta]'); if (!cta) return;
      const v = this.variant;
      if (!v?.available || !paypalUrl(v.paypalUrl) || cta.getAttribute('aria-disabled') === 'true') { event.preventDefault(); return; }
      cta.href = v.paypalUrl;
      const data = { ...this.eventData(), payment_provider: 'paypal' };
      track('dropky_buy_now_click', data);
      track('dropky_paypal_outbound', data);
      if (!event.ctrlKey && !event.metaKey && !event.shiftKey && event.button === 0) {
        cta.setAttribute('aria-busy', 'true'); cta.querySelector('[data-buy-label]').textContent = 'Continuăm pe PayPal…';
      }
    }
    initSticky() {
      this.sticky = this.querySelector('[data-sticky-buy]');
      const main = this.querySelector('[data-purchase] [data-paypal-cta]');
      if (!this.sticky || !main || !window.IntersectionObserver) return;
      this.mainVisible = true; this.pageVisible = true;
      const update = () => {
        const show = !this.mainVisible && this.pageVisible && matchMedia('(max-width:760px)').matches;
        this.sticky.hidden = !show;
        document.body.classList.toggle('dk-has-sticky', show);
      };
      this.ctaObserver = new IntersectionObserver(entries => { this.mainVisible = entries[0].isIntersecting; update(); }, { threshold: 0 });
      this.pageObserver = new IntersectionObserver(entries => { this.pageVisible = entries[0].isIntersecting; update(); }, { threshold: 0 });
      this.ctaObserver.observe(main); this.pageObserver.observe(this);
      window.addEventListener('resize', update, { signal: this.abort.signal });
    }
    disconnectedCallback() { this.abort?.abort(); this.ctaObserver?.disconnect(); this.pageObserver?.disconnect(); document.body.classList.remove('dk-has-sticky'); }
  }
  for (const [name, element] of [['dk-header',DropkyHeader],['dk-search',DropkySearch],['dk-gallery',DropkyGallery],['dk-product',DropkyProduct]]) {
    if (!customElements.get(name)) customElements.define(name, element);
  }
  document.addEventListener('change', event => { if (event.target.matches('[data-auto-submit]')) event.target.form?.requestSubmit(); });
  document.addEventListener('click', event => {
    const anchor = event.target.closest('a[data-jump]'); if (!anchor) return;
    const target = document.getElementById(anchor.dataset.jump);
    if (target) { event.preventDefault(); target.scrollIntoView({ behavior: reducedMotion() ? 'instant' : 'smooth', block: 'start' }); }
  });
  if (location.hash === '#faq' || location.hash === '#selectie') {
    requestAnimationFrame(() => document.getElementById(location.hash.slice(1))?.scrollIntoView({ behavior: 'instant' }));
  }
})();
